import { useState } from 'react';
import useLang from '../hooks/useLang';

/**
 * EvolutionBanner — panneau d'évolution version-à-version, charté comme
 * les autres panneaux de la colonne droite (Intention artistique, Plan
 * d'action) : card sur fond `--card` avec halo coloré bottom-right,
 * eyebrow mono uppercase + dot, structure cliquable / expand inline.
 *
 * Props :
 *   - evolution : objet retourné par l'API
 *       { resume, progres[], regressions[], persistants[], nouveaux[], dominante }
 *   - previousVersionName : ex "v1" — affiché en titre du panneau ("DEPUIS V1").
 *
 * Items purement informatifs (non cliquables). Si rien à dire, le composant
 * renvoie null.
 */
export default function EvolutionBanner({ evolution, previousVersionName, floorApplied = null, adviceLockApplied = null }) {
  const { s } = useLang();
  const [open, setOpen] = useState(false);

  if (!evolution) return null;
  const {
    resume = '',
    progres = [],
    regressions = [],
    persistants = [],
    nouveaux = [],
    dominante = 'neutre',
  } = evolution;

  const hasAnyItem =
    progres.length + regressions.length + persistants.length + nouveaux.length > 0;
  if (!resume && !hasAnyItem) return null;

  // Couleurs alignées sur les vars CSS de la fiche (voir MockupStyles.jsx).
  const haloColor =
    dominante === 'progres' ? 'var(--mint, #8ee07a)'
    : dominante === 'regressions' ? 'var(--red, #ff5d5d)'
    : 'var(--amber, #f5a623)';

  // Le titre du panneau : on s'aligne avec "INTENTION ARTISTIQUE" / "PLAN
  // D'ACTION" — texte court, mono uppercase, dot ambré en préfixe.
  // i18n via strings.js — bascule auto FR/EN.
  const titleText = previousVersionName
    ? (s.fiche?.evolutionSince || 'Évolution depuis {prev}').replace('{prev}', previousVersionName)
    : (s.fiche?.evolutionSinceLast || 'Évolution depuis la dernière');

  // Compteurs compacts à droite — chips colorés avec rotations
  // subtiles façon stickers (refonte 2026-04-30 "page plus jeune").
  // Chaque chip a sa couleur tinted bg + border + rotation différente.
  const chips = [];
  if (progres.length) chips.push({
    key: 'p', txt: `${progres.length}↑`,
    color: '#8ee07a', bg: 'rgba(142,224,122,0.10)', border: 'rgba(142,224,122,0.40)',
    rot: '-2deg',
  });
  if (regressions.length) chips.push({
    key: 'r', txt: `${regressions.length}↓`,
    color: '#ef6b6b', bg: 'rgba(239,107,107,0.10)', border: 'rgba(239,107,107,0.40)',
    rot: '1.5deg',
  });
  if (persistants.length) chips.push({
    key: 's', txt: `${persistants.length}→`,
    color: 'rgba(255,255,255,0.62)', bg: 'rgba(255,255,255,0.04)', border: 'rgba(255,255,255,0.18)',
    rot: '-1deg',
  });
  if (nouveaux.length) chips.push({
    key: 'n', txt: `+${nouveaux.length}`,
    color: '#f5a623', bg: 'rgba(245,166,35,0.12)', border: 'rgba(245,166,35,0.42)',
    rot: '2deg',
  });

  return (
    <section
      style={{
        // Card identique à .intent-panel-fiche / .plan-panel pour homogénéité
        // visuelle dans la colonne droite (cf. MockupStyles.jsx).
        position: 'relative',
        overflow: 'hidden',
        background: 'var(--card, #15151a)',
        border: '1px solid var(--border, rgba(255,255,255,0.08))',
        borderRadius: 14,
        padding: '20px 22px',
        width: '100%',
        boxSizing: 'border-box',
      }}
      aria-label={s.fiche?.evolutionAriaLabel || 'Évolution depuis la version précédente'}
    >
      {/* Halo coloré bottom-right (couleur = dominante de l'évolution) */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          bottom: -60,
          right: -60,
          width: 220,
          height: 220,
          borderRadius: '50%',
          background: haloColor,
          filter: 'blur(80px)',
          opacity: 0.12,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Eyebrow cliquable — structure alignée sur .intent-panel-eyebrow.
          Le className `evo-eyebrow` permet aux media queries (cf.
          MockupStyles) de réagencer le layout en mobile (chips wrap
          sous la ligne titre + résumé qui peut s'étaler sur 2 lignes). */}
      <button
        type="button"
        className="evo-eyebrow"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        style={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'transparent',
          border: 0,
          padding: 0,
          margin: 0,
          cursor: hasAnyItem ? 'pointer' : 'default',
          textAlign: 'left',
          color: 'inherit',
          font: 'inherit',
        }}
      >
        <span
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 4,
            minWidth: 0,
            flex: 1,
          }}
        >
          {/* Titre : chip pill cerulean (charté comme le titre
              "DIAGNOSTIC PAR ÉLÉMENTS" — même padding, border, bg). */}
          <span
            style={{
              alignSelf: 'flex-start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              fontFamily: 'var(--mono, JetBrains Mono, monospace)',
              fontSize: 10.5,
              letterSpacing: 2.2,
              textTransform: 'uppercase',
              color: '#5cb8cc',
              padding: '5px 12px 5px 10px',
              borderRadius: 999,
              background: 'rgba(92,184,204,0.06)',
              border: '1px solid rgba(92,184,204,0.30)',
              boxShadow: '0 6px 18px -10px rgba(0,0,0,0.55)',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#5cb8cc',
                flexShrink: 0,
              }}
            />
            <span style={{ whiteSpace: 'nowrap' }}>{titleText}</span>
          </span>

          {/* Sous-titre : le résumé qualitatif. Aligné sur le texte du titre
              (padding-left = dot 6px + gap 10px = 16px), tronqué si trop long. */}
          {resume && (
            <span
              style={{
                paddingLeft: 16,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                lineHeight: 1.4,
                color: 'var(--text, #ededed)',
                fontWeight: 300,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
              }}
              title={resume}
            >
              {resume}
            </span>
          )}
        </span>

        {/* Chips compactes (n↑ n↓ n→ +n) — stickers colorés avec
            rotations subtiles, langage néon cohérent avec les chips
            BPM/Key/Genre du panneau verdict. */}
        {chips.length > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {chips.map((c) => (
              <span
                key={c.key}
                style={{
                  display: 'inline-flex', alignItems: 'center',
                  fontFamily: 'var(--mono, JetBrains Mono, monospace)',
                  fontSize: 10.5, fontWeight: 600,
                  letterSpacing: 0.6,
                  padding: '3px 9px',
                  borderRadius: 999,
                  color: c.color,
                  background: c.bg,
                  border: `1px solid ${c.border}`,
                  fontVariantNumeric: 'tabular-nums',
                  transform: `rotate(${c.rot})`,
                  transition: 'transform .2s ease',
                  boxShadow: '0 6px 18px -10px rgba(0,0,0,0.55)',
                }}
              >
                {c.txt}
              </span>
            ))}
          </span>
        )}

        {/* Chevron pointant à DROITE par défaut (comme .points-forts /
            .à-travailler), rotate 90° quand ouvert pour pointer en bas. */}
        {hasAnyItem && (
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--amber, #f5a623)',
              marginLeft: 4,
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform .2s ease',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M4.5 3l3 3-3 3"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
      </button>

      {/* Body — listes courtes alignées sur le texte du titre (padding-left 16px) */}
      {open && hasAnyItem && (
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            marginTop: 14,
            paddingLeft: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <EvolutionList label={s.fiche?.evolutionProgres || 'Progrès'} items={progres} color="var(--mint, #8ee07a)" />
          <EvolutionList label={s.fiche?.evolutionRegressions || 'Régressions'} items={regressions} color="var(--red, #ff5d5d)" />
          <EvolutionList label={s.fiche?.evolutionPersistants || 'Persistants'} items={persistants} color="var(--muted, rgba(255,255,255,0.5))" />
          <EvolutionList label={s.fiche?.evolutionNouveaux || 'Nouveaux'} items={nouveaux} color="var(--amber, #f5a623)" />
          {(floorApplied || adviceLockApplied) && (
            <div
              style={{
                marginTop: 4,
                paddingTop: 10,
                borderTop: '1px dashed rgba(255,255,255,0.08)',
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11.5,
                lineHeight: 1.55,
                color: 'var(--muted, rgba(255,255,255,0.5))',
                fontStyle: 'italic',
                display: 'flex',
                flexDirection: 'column',
                gap: 4,
              }}
            >
              {floorApplied && (
                <span>
                  {typeof floorApplied === 'object' && floorApplied.highCount != null
                    ? `Plafond actif : ${floorApplied.ceiling}/100 tant que ${floorApplied.highCount} item${floorApplied.highCount > 1 ? 's' : ''} priorité haute restent à traiter.`
                    : 'Plancher de stabilité actif — les régressions courtes sont lissées entre versions.'}
                </span>
              )}
              {adviceLockApplied && (
                <span>
                  {(() => {
                    const f = adviceLockApplied.followed || 0;
                    const u = adviceLockApplied.unfollowed || 0;
                    const cats = adviceLockApplied.categories || [];
                    const pieces = [];
                    if (f > 0) pieces.push(`${f} conseil${f > 1 ? 's' : ''} confirmé${f > 1 ? 's' : ''}`);
                    if (u > 0) pieces.push(`${u} encore présent${u > 1 ? 's' : ''}`);
                    const head = pieces.length ? pieces.join(' · ') : 'Items cochés vérifiés';
                    if (cats.length) {
                      return `${head} — sub-score${cats.length > 1 ? 's' : ''} verrouillé${cats.length > 1 ? 's' : ''} sur ${cats.map((c) => c.cat).join(' · ')}.`;
                    }
                    return `${head} — les sub-scores des items cochés ne peuvent pas régresser.`;
                  })()}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function EvolutionList({ label, items, color }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontFamily: 'var(--mono, JetBrains Mono, monospace)',
          fontSize: 10,
          letterSpacing: 1.4,
          color,
          textTransform: 'uppercase',
        }}
      >
        {label}
      </span>
      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 3 }}>
        {items.map((it, i) => (
          <li
            key={i}
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              lineHeight: 1.55,
              color: 'var(--text, #c5c5c7)',
              fontWeight: 300,
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
            }}
          >
            <span style={{ color, marginTop: 1, flexShrink: 0 }}>·</span>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
